import { useEffect, useState } from 'react';
import { purchasingAPI, ContractPrice, ContractType, fetchAllPages } from '../../lib/api';
import { usePreferences } from '../../contexts/PreferencesContext';
import { Button } from '../ui/button';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';

interface ContractPricebookEditFormProps {
  contract: ContractPrice;
  /** Fired after a successful save so the detail modal and list refetch. */
  onSaved: () => void;
}

/**
 * CR-6424 (R3): the pricebook detail pop-up's "Edit Contract" tab, as an
 * actual edit form.
 *
 * The legacy platform's Purchased Contracted Prices pop-up — the ticket's
 * named reference — makes this tab an inline edit form; ours was a read-only
 * display, a tab called "Edit Contract" in which nothing was editable. The
 * form covers the contract-header fields (validity window, currency, status,
 * notes, and — Customer review 11 Sep, item 23 — the price book type);
 * identity fields (number, customer, supplier) are shown read-only, and parts
 * keep their own tab and the full edit modal.
 *
 * @link ContractPriceDetailModal mounts this in pricebook mode only.
 */
export default function ContractPricebookEditForm({
  contract,
  onSaved,
}: ContractPricebookEditFormProps) {
  const { formatDate } = usePreferences();
  const [form, setForm] = useState({
    supplier_contract_reference: contract.supplier_contract_reference || '',
    valid_from: contract.valid_from,
    valid_to: contract.valid_to,
    currency: contract.currency,
    status: contract.status,
    notes: contract.notes || '',
    contract_type: contract.contract_type ? String(contract.contract_type) : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Customer review 11 Sep (item 23): the price book type is chosen here.
  const [contractTypes, setContractTypes] = useState<ContractType[]>([]);
  const [typesError, setTypesError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchAllPages<ContractType>((page) => purchasingAPI.getContractTypes({ enabled: true, page_size: 100, page }))
      .then((list) => { if (!cancelled) setContractTypes(list); })
      .catch((err: unknown) => {
        console.error('Failed to load contract types:', err);
        if (!cancelled) setTypesError('Could not load the price book types — the current type is kept.');
      });
    return () => { cancelled = true; };
  }, []);

  // The contract's own type may belong to another company (the reviewed tenant's imported
  // contracts were typed with the platform's types) or be disabled — keep it
  // selectable as the current value so the picker never shows a wrong label.
  const typeOptions: ContractType[] = contract.contract_type && !contractTypes.some((t) => t.id === contract.contract_type)
    ? [{ id: contract.contract_type, name: contract.contract_type_name || `#${contract.contract_type}`, abbreviation: contract.contract_type_abbreviation || '', enabled: false }, ...contractTypes]
    : contractTypes;
  const typeChanged = form.contract_type !== (contract.contract_type ? String(contract.contract_type) : '');

  const handleChange = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Same rule as the create/edit modal: while the validity window covers
  // today the backend derives status itself, so the picker would be a lie.
  const today = new Date().toISOString().slice(0, 10);
  const inCurrentWindow = Boolean(
    form.valid_from && form.valid_to
    && form.valid_from <= today && today <= form.valid_to
  );

  const handleSave = async () => {
    if (form.valid_from && form.valid_to && form.valid_to < form.valid_from) {
      setError('Valid To must be on or after Valid From.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await purchasingAPI.updateContractPrice(contract.id, {
        supplier_contract_reference: form.supplier_contract_reference.trim(),
        valid_from: form.valid_from,
        valid_to: form.valid_to,
        currency: form.currency,
        status: form.status as ContractPrice['status'],
        notes: form.notes,
        // Only a real change is sent — the number is never regenerated.
        ...(typeChanged && form.contract_type ? { contract_type: parseInt(form.contract_type, 10) } : {}),
      });
      toast.success('Contract updated successfully');
      onSaved();
    } catch (err: unknown) {
      console.error('Failed to update contract:', err);
      const message = getApiErrorMessage(err, 'Failed to update contract');
      setError(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider">Contract Number</label>
          <p className="font-mono font-semibold text-lg">{contract.contract_number}</p>
        </div>
        <div>
          {/* CR-7267: the supplier's own reference, beside ours and labelled so nobody confuses the two. Optional. */}
          <label htmlFor="supplier-contract-reference" className="text-xs text-muted-foreground uppercase tracking-wider block mb-1">Supplier's Contract Reference</label>
          <input
            id="supplier-contract-reference"
            type="text"
            value={form.supplier_contract_reference}
            onChange={(e) => handleChange('supplier_contract_reference', e.target.value)}
            maxLength={100}
            placeholder="The supplier's own number for this agreement (optional)"
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          {/* Customer review 11 Sep (item 23): Standard / Contract / Accounting. */}
          <label htmlFor="pricebook-contract-type" className="text-xs text-muted-foreground uppercase tracking-wider block mb-1">Price Book / Contract Type</label>
          <select
            id="pricebook-contract-type"
            value={form.contract_type}
            onChange={(e) => handleChange('contract_type', e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {!contract.contract_type && <option value="">Not set — pick a type</option>}
            {typeOptions.map((t) => (
              <option key={t.id} value={String(t.id)}>
                {t.name} ({t.abbreviation}){t.id === contract.contract_type ? ' — current' : ''}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            {typesError || (typeChanged
              ? `The contract number stays ${contract.contract_number} — changing the type does not renumber it.`
              : 'The contract number is never renumbered when the type changes.')}
          </p>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider">Customer</label>
          <p className="font-medium">{contract.customer_name || 'N/A'}</p>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider">Supplier</label>
          <p className="font-medium">{contract.supplier_name || 'N/A'}</p>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1">Valid From</label>
          <input
            type="date"
            value={form.valid_from}
            onChange={(e) => handleChange('valid_from', e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1">Valid To</label>
          <input
            type="date"
            value={form.valid_to}
            onChange={(e) => handleChange('valid_to', e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1">Currency</label>
          <select
            value={form.currency}
            onChange={(e) => handleChange('currency', e.target.value)}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="GBP">GBP - British Pound</option>
            <option value="USD">USD - US Dollar</option>
            <option value="EUR">EUR - Euro</option>
            <option value="JPY">JPY - Japanese Yen</option>
            <option value="CNY">CNY - Chinese Yuan</option>
            <option value="INR">INR - Indian Rupee</option>
            <option value="AUD">AUD - Australian Dollar</option>
            <option value="CAD">CAD - Canadian Dollar</option>
            <option value="CHF">CHF - Swiss Franc</option>
            <option value="MXN">MXN - Mexican Peso</option>
            <option value="PLN">PLN - Polish Złoty</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1">
            Status
            {inCurrentWindow && (
              <span className="normal-case tracking-normal font-normal ml-1">
                — auto-derived while the dates cover today
              </span>
            )}
          </label>
          <select
            value={form.status}
            onChange={(e) => handleChange('status', e.target.value)}
            disabled={inCurrentWindow}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
          >
            <option value="draft">Draft</option>
            {/* CR-6424 (R4): the pricebook surface calls this status "Live". */}
            <option value="active">Live</option>
            <option value="expired">Expired</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider">Created By</label>
          <p className="font-medium">{contract.created_by_name || 'N/A'}</p>
        </div>
        <div>
          <label className="text-xs text-muted-foreground uppercase tracking-wider">Created At</label>
          <p className="font-medium">{formatDate(contract.created_at)}</p>
        </div>
        <div className="md:col-span-2 lg:col-span-3">
          <label className="text-xs text-muted-foreground uppercase tracking-wider block mb-1">Notes</label>
          <textarea
            value={form.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      {error && (
        <div className="mt-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm whitespace-pre-wrap break-words">
          {error}
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <Save className="h-4 w-4 mr-1" />
          )}
          Save Changes
        </Button>
      </div>
    </div>
  );
}
